import { type ReactNode, useCallback } from 'react'
import { type ShouldBlockFn, useBlocker } from '@tanstack/react-router'
import { useDisclosure, useWindowEvent } from '@mantine/hooks'
import { type UseFormReturnType } from '@mantine/form'
import { Group, Modal, Stack, Text } from '@mantine/core'
import { useTranslation } from '@front/model'
import { MaterialSymbolIcon } from './terros/icon/MaterialSymbolIcon'
import { shouldBlockFormNavigation } from './formNavigationBlocker'
import { TButton } from './buttons'

type Props = {
  form: Pick<UseFormReturnType<any>, 'isDirty' | 'submitting'>
  onDiscard?: VoidFunction
  allowSamePathNavigation?: boolean
}

export const FormBlocker = ({ form, onDiscard, allowSamePathNavigation = false }: Props): ReactNode => {
  const shouldBlock = (): boolean => form.isDirty() && !form.submitting
  const { t } = useTranslation()

  const [opened, { close, open }] = useDisclosure(true, {})

  const shouldBlockFn: ShouldBlockFn = useCallback(
    ({ current, next }) =>
      shouldBlockFormNavigation({
        isDirty: form.isDirty(),
        isSubmitting: form.submitting,
        allowSamePathNavigation,
        currentPathname: current.pathname,
        nextPathname: next.pathname,
      }),
    [form, allowSamePathNavigation]
  )

  const { proceed, reset, status } = useBlocker({ shouldBlockFn, withResolver: true })
  useWindowEvent('beforeunload', (e) => {
    if (shouldBlock()) e.preventDefault()
  })

  if (status === 'blocked') {
    return (
      <Modal
        opened={opened}
        onClose={close}
        onExitTransitionEnd={() => {
          reset()
          open()
        }}
        title={
          <Group>
            <MaterialSymbolIcon icon='warning' />
            {t('Unsaved Changes')}
          </Group>
        }
      >
        <Stack>
          <Text>{t('You have unsaved changes. Are you sure you want to leave?')}</Text>
          <Group align='flex-start' justify='right' grow>
            <TButton
              variant='destructive'
              onClick={() => {
                close()
                if (onDiscard) onDiscard()
                proceed()
              }}
            >
              {t('Discard Changes')}
            </TButton>
            <TButton variant='primary' onClick={close}>
              {t('Stay on Page')}
            </TButton>
          </Group>
        </Stack>
      </Modal>
    )
  }
}
